import { T, useGT } from 'gt-react';
import { MAX_CREDENTIAL_AUTO_PAUSE_REJECTED_RUNS } from '@erp/shared-types';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export function CredentialAutoPauseFields({
  enabled,
  threshold,
  error,
  onEnabledChange,
  onThresholdChange,
}: {
  enabled: boolean;
  threshold: string;
  error?: string;
  onEnabledChange: (enabled: boolean) => void;
  onThresholdChange: (threshold: string) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex flex-col gap-3 rounded-md border p-3">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="credentialAutoPause">
          <T context="Credential setting for automatically pausing agent resources">
            Auto-Pause After Rejected Runs
          </T>
        </Label>
        <Switch
          id="credentialAutoPause"
          checked={enabled}
          onCheckedChange={onEnabledChange}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        <T context="Explanation of credential auto-pause policy shared by agent resources">
          Off by default. When enabled, all agent resources using this
          credential pause after the configured number of consecutive rejected
          runs. Timeouts and infrastructure failures do not count. Credential
          replacement or a verified successful sign-in resets the count.
        </T>
      </p>
      {enabled && (
        <div className="flex flex-col gap-2" data-invalid={Boolean(error)}>
          <Label htmlFor="credentialAutoPauseThreshold">
            <T context="Number of distinct rejected runs before automatically pausing resources">
              Rejected Runs Before Pausing
            </T>
          </Label>
          <Input
            id="credentialAutoPauseThreshold"
            type="number"
            min={1}
            max={MAX_CREDENTIAL_AUTO_PAUSE_REJECTED_RUNS}
            step={1}
            value={threshold}
            aria-invalid={Boolean(error)}
            aria-label={gt('Rejected Runs Before Pausing', {
              context: 'Accessible label for credential auto-pause threshold',
            })}
            onChange={(event) => onThresholdChange(event.target.value)}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )}
    </div>
  );
}
